
import React, { useState, useEffect } from 'react';
import { Terminal, Github, Mail, AlertCircle } from 'lucide-react';
import { Language } from '../types';
import { getTranslation } from '../lib/translations';
import { loginWithSocial, loginWithEmail, supabase } from '../services/supabaseService';

interface AuthPageProps {
    lang: Language;
    initialError?: string | null;
}

const AuthPage: React.FC<AuthPageProps> = ({ lang, initialError }) => {
    const [isLoading, setIsLoading] = useState<string | null>(null);
    const [email, setEmail] = useState("");
    const [errorMsg, setErrorMsg] = useState<string | null>(initialError || null);
    const [infoMsg, setInfoMsg] = useState<string | null>(null);
    const t = getTranslation(lang);

    useEffect(() => {
        if (initialError) {
            setErrorMsg(initialError);
        }
    }, [initialError]);

    const handleRealLogin = async (provider: 'google' | 'github' | 'linuxdo' | 'email') => {
        setIsLoading(provider);
        setErrorMsg(null);
        setInfoMsg(null);

        try {
            if (provider === 'email') {
                if (!email) throw new Error("Please enter an email address");
                await loginWithEmail(email);
                setInfoMsg(lang === 'zh' ? '登录链接已发送至您的邮箱，请查收。' : 'Magic link sent! Check your email.');
                setIsLoading(null);
            } else if (provider === 'linuxdo') {
                // Redirect to our Vercel Serverless Function
                window.location.href = '/api/linuxdo-login';
            } else {
                await loginWithSocial(provider);
                // Redirect happens automatically
            }
        } catch (error: any) {
            console.error("Login Failed:", error);
            setErrorMsg(error.message || "Authentication failed");
            setIsLoading(null);
        }
    };

    const handleClick = (provider: 'google' | 'github' | 'linuxdo' | 'email') => {
        // STRICT CHECK: If supabase client is missing (env vars not set), block login.
        // Exception: Linux.do flow is handled by server-side function which checks its own envs.
        if (!supabase && provider !== 'linuxdo') {
            setErrorMsg(lang === 'zh' 
                ? "配置错误：未检测到 Supabase 环境变量，无法进行真实登录。" 
                : "Configuration Error: Supabase environment variables missing. Real auth unavailable.");
            return;
        }
        handleRealLogin(provider);
    };

    return (
        <div className="min-h-screen w-full bg-background flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full bg-surface border border-border rounded-xl p-8 shadow-2xl relative overflow-hidden">
                {/* Decorative background glow */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-secondary"></div>
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>

                <div className="flex flex-col items-center text-center mb-8 relative z-10">
                    <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mb-4 border border-zinc-800 shadow-inner">
                        <Terminal size={32} className="text-primary" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">{t.loginTitle}</h1>
                    <p className="text-muted text-sm">{t.loginSubtitle}</p>
                </div>

                {errorMsg && (
                    <div className="mb-4 p-3 bg-red-950/30 border border-red-900/50 rounded flex items-center gap-2 text-xs text-red-400 break-words w-full">
                        <AlertCircle size={14} className="shrink-0" />
                        <span>{errorMsg}</span>
                    </div>
                )}

                {infoMsg && (
                    <div className="mb-4 p-3 bg-emerald-950/30 border border-emerald-900/50 rounded flex items-center gap-2 text-xs text-emerald-400">
                        <Terminal size={14} />
                        {infoMsg}
                    </div>
                )}

                <div className="space-y-4 relative z-10">
                    {/* Google Login */}
                    <button
                        disabled={!!isLoading}
                        onClick={() => handleClick('google')}
                        className="w-full h-11 bg-white hover:bg-zinc-100 text-black font-medium rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading === 'google' ? (
                            <div className="w-5 h-5 border-2 border-zinc-300 border-t-black rounded-full animate-spin"></div>
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" />
                                <path fill="#EA4335" d="M12 4.66c1.61 0 3.06.56 4.21 1.64l3.16-3.16C17.45 1.18 14.97 0 12 0 7.7 0 3.99 2.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                        )}
                        {t.continueGoogle}
                    </button>

                    {/* GitHub Login */}
                    <button
                        disabled={!!isLoading}
                        onClick={() => handleClick('github')}
                        className="w-full h-11 bg-[#24292e] hover:bg-[#2f363d] text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                         {isLoading === 'github' ? (
                            <div className="w-5 h-5 border-2 border-zinc-600 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <Github size={20} />
                        )}
                        {t.continueGithub}
                    </button>

                    {/* Linux.do Login */}
                    <button
                        disabled={!!isLoading}
                        onClick={() => handleClick('linuxdo')}
                        className="w-full h-11 bg-[#1e2025] hover:bg-[#2a2d33] border border-zinc-700 text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                         {isLoading === 'linuxdo' ? (
                            <div className="w-5 h-5 border-2 border-zinc-600 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <clipPath id="linuxdo-logo-clip">
                                        <circle cx="60" cy="60" r="47"/>
                                    </clipPath>
                                </defs>
                                <circle fill="#f0f0f0" cx="60" cy="60" r="50"/>
                                <rect fill="#1c1c1e" clipPath="url(#linuxdo-logo-clip)" x="10" y="10" width="100" height="30"/>
                                <rect fill="#f0f0f0" clipPath="url(#linuxdo-logo-clip)" x="10" y="40" width="100" height="40"/>
                                <rect fill="#ffb003" clipPath="url(#linuxdo-logo-clip)" x="10" y="80" width="100" height="30"/>
                            </svg>
                        )}
                        {t.continueLinuxdo}
                    </button>
                    
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-surface px-2 text-muted">Or</span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <input 
                            type="email" 
                            placeholder={t.emailPlaceholder}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-11 bg-zinc-900/50 border border-border rounded-lg px-4 text-sm text-white focus:outline-none focus:border-primary transition-colors"
                        />
                        <button
                            disabled={!!isLoading}
                            onClick={() => handleClick('email')}
                            className="w-full h-11 bg-transparent border border-border hover:bg-zinc-900 text-zinc-300 hover:text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading === 'email' ? (
                                <div className="w-5 h-5 border-2 border-zinc-600 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <Mail size={18} />
                            )}
                            {t.continueEmail}
                        </button>
                    </div>
                </div>
                
                <div className="mt-8 text-center">
                    <p className="text-xs text-zinc-600 font-mono">protocol_omega // v1.0.0</p>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;
